import { decode, encode } from '@googlemaps/polyline-codec';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { FaCheck, FaPencilAlt } from 'react-icons/fa';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import { computeRoute } from 'features/Responder/features/Tours/services/googleRoutes';
import RouteSummaryBar from './components/RouteSummaryBar';
import StartLocationCard from './components/StartLocationCard';
import StopList from './components/StopList';
import { useAutoBuild } from './hooks/useAutoBuild';
import { useBuilderHydration } from './hooks/useBuilderHydration';
import { buildIndoorPlacement } from './hooks/useIndoorPlacement';
import { useRouteCalculations } from './hooks/useRouteCalculations';
import { useRoutePersistence } from './hooks/useRoutePersistence';
import { getClosestPointOnPolyline } from './utils/closestPointOnPolyline';
import { getDistanceToPolyline } from './utils/distanceToPolyline';
import emptyCheckpoint from './utils/emptyCheckpoint';
import googleApiKeyLookup from './utils/googleAPiKeyLookup';
import normalizeTravelMode from './utils/normalizeTravelMode';
import { getZonePolygonPoints, isZoneGeometry, resolveRouteStop } from './utils/routeStopGeometry';
import { clipPolylineAtZone, polylineIntersectsPolygon } from './utils/zoneRouteMath';

const TAIL_DISTANCE_METERS = 10;
const STOP_MATCH_TOLERANCE = 1e-5;
const LOOP_RETURN_DISTANCE_THRESHOLD_METERS = 80;

const toNonEmptyString = (value) => {
  if (value == null) return null;
  const normalized = `${value}`.trim();
  return normalized.length ? normalized : null;
};

const resolveStopZoneId = (stop = {}) =>
  stop?.zoneId ?? stop?.zone_id ?? stop?.geometry?.zoneId ?? null;

const distanceMetersBetween = (a, b) => {
  if (!a || !b) return null;
  const aLat = Number(a?.lat);
  const aLng = Number(a?.lng);
  const bLat = Number(b?.lat);
  const bLng = Number(b?.lng);
  if (
    !Number.isFinite(aLat) ||
    !Number.isFinite(aLng) ||
    !Number.isFinite(bLat) ||
    !Number.isFinite(bLng)
  ) {
    return null;
  }

  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const stopsRepresentSameLocation = (a, b) => {
  if (!a || !b) return false;

  const aId = toNonEmptyString(a?.id);
  const bId = toNonEmptyString(b?.id);
  if (aId && bId && aId === bId) return true;

  const aZone = toNonEmptyString(resolveStopZoneId(a));
  const bZone = toNonEmptyString(resolveStopZoneId(b));
  if (aZone && bZone && aZone === bZone) return true;

  const aPlaceId = toNonEmptyString(a?.placeId);
  const bPlaceId = toNonEmptyString(b?.placeId);
  if (aPlaceId && bPlaceId && aPlaceId === bPlaceId) return true;

  const aLat = Number(a?.lat);
  const aLng = Number(a?.lng);
  const bLat = Number(b?.lat);
  const bLng = Number(b?.lng);
  if (
    Number.isFinite(aLat) &&
    Number.isFinite(aLng) &&
    Number.isFinite(bLat) &&
    Number.isFinite(bLng)
  ) {
    return (
      Math.abs(aLat - bLat) <= STOP_MATCH_TOLERANCE && Math.abs(aLng - bLng) <= STOP_MATCH_TOLERANCE
    );
  }

  return false;
};

const isReturnLikeStop = (stop = {}) => {
  const kind = `${stop?.kind ?? stop?.type ?? ''}`.toLowerCase().trim();
  return (
    kind === 'destination' ||
    kind === 'origin' ||
    kind === 'end' ||
    kind === 'return' ||
    kind === 'return_to_start' ||
    kind === 'loop_return'
  );
};

const pruneTrailingLoopReturnStop = (stops = [], origin = null) => {
  if (!Array.isArray(stops) || stops.length <= 1) return stops;
  const tail = stops[stops.length - 1];
  const distanceToOriginMeters = distanceMetersBetween(origin, tail);
  const sameLocation = stopsRepresentSameLocation(origin, tail);
  const returnLikeNearOrigin =
    isReturnLikeStop(tail) &&
    (sameLocation ||
      (Number.isFinite(distanceToOriginMeters) &&
        distanceToOriginMeters <= LOOP_RETURN_DISTANCE_THRESHOLD_METERS));
  const shouldRemoveTail = sameLocation || returnLikeNearOrigin;

  return shouldRemoveTail ? stops.slice(0, -1) : stops;
};

/**
 * TourBuilder renders the route builder panel for responders.
 * Handles stop editing, route building, and checkpoint projection.
 * @returns {JSX.Element}
 */
export default function TourBuilder({
  className = '',
  style,
  onPersistSuccess = () => {},
  onCancelEdit = () => {},
} = {}) {
  const {
    setActiveTourData,
    setRouteData,
    setStops,
    setStartLocation,
    setCenter,
    routeData,
    checkpoints,
    setCheckpoints,
    setSelectedCheckpointId,
    setMapTool,
    stops,
    startLocation,
    activeTourData,
    tours,
    builderTourData,
    setBuilderTourData,
    builderHydrationRef,
    builderResetSignal,
    resetBuilderState,
    snapToRoads,
    addTour,
    updateBuilderTourMeta,
  } = useTours();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [autoBuildPending, setAutoBuildPending] = useState(false);
  const [usingCurrentStart, setUsingCurrentStart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [editingTourId, setEditingTourId] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isLooped, setIsLooped] = useState(true);
  const lastStartSigRef = useRef(null);
  const lastCheckpointSigForRoute = useRef(null);
  const scrollContainerRef = useRef(null);
  const lastStopCountRef = useRef(stops?.length ?? 0);
  const latestBuildRequestIdRef = useRef(0);
  const nameInputRef = useRef(null);
  const lastResetSignalRef = useRef(builderResetSignal);

  const resetBuilder = () => {
    if (builderHydrationRef) builderHydrationRef.current = '__blocked__';
    if (typeof resetBuilderState === 'function') {
      resetBuilderState();
    } else {
      setActiveTourData(null);
      setBuilderTourData(null);
      setRouteData(null);
      setCheckpoints([]);
      setStops([]);
      setStartLocation({
        address: '',
        lat: '',
        lng: '',
        placeId: '',
        useCurrent: false,
        zoneId: null,
        geometry: null,
        returnMode: 'DRIVING',
      });
      setSelectedCheckpointId(null);
      setMapTool(null);
      setCenter(null);
    }
    setEditingTourId(null);
    setUsingCurrentStart(false);
    setSaveSuccess('');
    setSaveError('');
    setIsLooped(true);
    setSaveName('');
  };

  useEffect(() => {
    if (lastResetSignalRef.current === builderResetSignal) return;
    lastResetSignalRef.current = builderResetSignal;
    resetBuilder();
  }, [builderResetSignal]);

  useBuilderHydration({
    builderTourData,
    builderHydrationRef,
    setStartLocation,
    setStops,
    setRouteData,
    setEditingTourId,
    setSaveSuccess,
    setSaveError,
    setSelectedCheckpointId,
    setIsLooped,
  });

  const googleApiKey = googleApiKeyLookup();

  const {
    isIndoor,
    isFreePlacement,
    canBuild,
    canSaveRoute,
    checkpointSignature,
    routeWaypointOrder,
    stopEtaSeconds,
    stopMetaById,
    checkpointMetaById,
    tourMiles,
    tourEstimateLabel,
  } = useRouteCalculations({
    startLocation,
    stops,
    routeData,
    checkpoints,
    builderTourData,
    snapToRoads,
  });

  const clearRouteArtifacts = () => {
    setRouteData(null);
    setActiveTourData(null);
    setSelectedCheckpointId(null);
    setSaveSuccess('');
    setSaveError('');
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    setStops((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIndex(idx);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setAutoBuildPending(true);
  };

  const canAddStop = useMemo(() => {
    if (!stops?.length) return true;
    const lastStop = stops[stops.length - 1];
    const hasAddress = typeof lastStop?.address === 'string' && lastStop.address.trim().length > 0;
    const hasPlaceId = typeof lastStop?.placeId === 'string' && lastStop.placeId.trim().length > 0;
    const lat = Number.parseFloat(lastStop?.lat);
    const lng = Number.parseFloat(lastStop?.lng);
    const hasLatLng = Number.isFinite(lat) && Number.isFinite(lng);
    return hasAddress || hasPlaceId || hasLatLng;
  }, [stops]);

  const addStopDisabledReason = canAddStop
    ? ''
    : 'Finish the previous checkpoint by adding an address or coordinates.';

  const updateStop = (id, field, value) => {
    setStops((prev) => prev.map((stop) => (stop.id === id ? { ...stop, [field]: value } : stop)));
    if (['lat', 'lng', 'address', 'placeId'].includes(field)) {
      setAutoBuildPending(true);
    }
  };

  const addStop = () => {
    setStops((prev) => {
      const fallbackMode = prev.length > 0 ? prev[prev.length - 1]?.mode || 'DRIVING' : 'DRIVING';
      return [
        ...prev,
        {
          ...emptyCheckpoint(prev.length),
          mode: fallbackMode,
        },
      ];
    });
    setAutoBuildPending(true);
  };

  const removeStop = (id) => {
    setStops((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (next.length === 0) {
        clearRouteArtifacts();
      } else {
        setAutoBuildPending(true);
      }
      return next;
    });
  };

  const handleBuildRoute = async () => {
    const requestId = latestBuildRequestIdRef.current + 1;
    latestBuildRequestIdRef.current = requestId;

    const hasLocation = (stop) => {
      if (!stop) return false;
      const lat = Number.parseFloat(stop.lat);
      const lng = Number.parseFloat(stop.lng);
      const hasLatLng = Number.isFinite(lat) && Number.isFinite(lng);
      const hasPlaceId = !!(stop.placeId && stop.placeId.trim());
      return hasPlaceId || hasLatLng;
    };

    setError('');
    setLoading(true);
    try {
      const allowAlternates = false;
      const hasExplicitStart = hasLocation(startLocation);

      // Route order follows the current list order.
      const orderedStops = [...stops];

      const origin = hasExplicitStart ? startLocation : orderedStops[0];
      let waypointStops = hasExplicitStart ? orderedStops : orderedStops.slice(1);
      if (!origin || !hasLocation(origin)) {
        throw new Error('Missing valid start location');
      }
      if (!waypointStops.length) {
        throw new Error('Add at least one checkpoint after start');
      }
      // Defensive: if a prior loop artifact left a trailing "return to start" checkpoint,
      // drop it when loop mode is disabled so route terminates at the true last checkpoint.
      if (!isLooped && waypointStops.length > 1) {
        const tail = waypointStops[waypointStops.length - 1];
        const distanceToOriginMeters = distanceMetersBetween(origin, tail);
        const sameLocation = stopsRepresentSameLocation(origin, tail);
        const returnLikeNearOrigin =
          isReturnLikeStop(tail) &&
          (sameLocation ||
            (Number.isFinite(distanceToOriginMeters) &&
              distanceToOriginMeters <= LOOP_RETURN_DISTANCE_THRESHOLD_METERS));
        const shouldRemoveTail = sameLocation || returnLikeNearOrigin;
        if (shouldRemoveTail) {
          waypointStops = waypointStops.slice(0, -1);
        }
      }
      if (!waypointStops.length) {
        throw new Error('Add at least one checkpoint after start');
      }

      const stopsForRoute = [origin, ...waypointStops];
      const routeStops = isLooped
        ? [
            ...stopsForRoute,
            {
              ...origin,
              mode: normalizeTravelMode(startLocation?.returnMode || origin?.mode),
            },
          ]
        : stopsForRoute;

      const toCoords = (stop) => {
        const lat = Number.parseFloat(stop.lat);
        const lng = Number.parseFloat(stop.lng);
        return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
      };

      if (isFreePlacement) {
        const { activeTrip } = buildIndoorPlacement(routeStops);
        setRouteData(null);
        lastCheckpointSigForRoute.current = checkpointSignature;
        setActiveTourData(activeTrip);
        return;
      }

      if (!googleApiKey) {
        throw new Error(
          'Missing Google Maps API key. Set PUBLIC_GOOGLE_MAPS_API_KEY or REACT_APP_GOOGLE_MAPS_API_KEY to build driving/walking routes.',
        );
      }

      const parseDurationSeconds = (duration) => {
        if (typeof duration === 'number') return duration;
        if (typeof duration === 'string') {
          const num = parseFloat(duration.replace('s', ''));
          return Number.isFinite(num) ? num : 0;
        }
        return 0;
      };

      const legsData = [];
      const combinedPoints = [];
      let totalDistanceMeters = 0;
      let totalDurationSeconds = 0;

      for (let i = 0; i < routeStops.length - 1; i++) {
        const from = routeStops[i];
        const to = routeStops[i + 1];
        const isFirstLeg = i === 0;
        const isLastLeg = i === routeStops.length - 2;
        const requestedMode = (to?.mode || '').toString().toUpperCase();
        const legTravelMode =
          requestedMode === 'WALK' || requestedMode === 'WALKING' ? 'WALK' : 'DRIVE';
        const legFrom = await resolveRouteStop(from);
        const legTo = await resolveRouteStop(to);
        if (!legFrom || !legTo) {
          throw new Error('Missing valid route coordinates for a stop.');
        }

        const legResult = await computeRoute({
          stops: [legFrom, legTo],
          travelMode: legTravelMode,
          apiKey: googleApiKey,
          extraOptions: allowAlternates ? { computeAlternativeRoutes: true } : {},
        });

        const legDetails =
          Array.isArray(legResult?.legs) && legResult.legs.length ? legResult.legs[0] : null;
        const legPolyline = legDetails?.polyline || legResult?.polyline || '';
        const distanceMeters = Number.isFinite(legDetails?.distanceMeters)
          ? legDetails.distanceMeters
          : Number.isFinite(legResult?.totalDistanceMeters)
            ? legResult.totalDistanceMeters
            : 0;
        const durationSeconds = parseDurationSeconds(
          legDetails?.duration ?? legResult?.totalDuration,
        );

        totalDistanceMeters += Number.isFinite(distanceMeters) ? distanceMeters : 0;
        totalDurationSeconds += Number.isFinite(durationSeconds) ? durationSeconds : 0;

        const decodedLeg = legPolyline ? decode(legPolyline) : [];
        let finalLegPoints = decodedLeg;
        const fromZone = isZoneGeometry(from) ? getZonePolygonPoints(from) : [];
        const toZone = isZoneGeometry(to) ? getZonePolygonPoints(to) : [];
        const fromIntersects =
          fromZone.length >= 3 && polylineIntersectsPolygon(decodedLeg, fromZone);
        const toIntersects = toZone.length >= 3 && polylineIntersectsPolygon(decodedLeg, toZone);
        const fromNeedsTail = isZoneGeometry(from) && !fromIntersects;
        const toNeedsTail = isZoneGeometry(to) && !toIntersects;
        if (decodedLeg.length && (isFirstLeg || isLastLeg)) {
          if (isFirstLeg && isZoneGeometry(from)) {
            if (fromNeedsTail && fromZone.length >= 3) {
              finalLegPoints = clipPolylineAtZone(finalLegPoints, fromZone, 'from');
            }
          }
          if (isLastLeg && isZoneGeometry(to)) {
            if (toNeedsTail && toZone.length >= 3) {
              finalLegPoints = clipPolylineAtZone(finalLegPoints, toZone, 'to');
            }
          }
        }
        const finalLegPolyline = finalLegPoints.length > 1 ? encode(finalLegPoints) : legPolyline;

        legsData.push({
          distanceMeters,
          duration: durationSeconds,
          polyline: finalLegPolyline,
          travelMode: legTravelMode === 'WALK' ? 'WALKING' : 'DRIVING',
        });

        if (finalLegPoints.length) {
          if (!combinedPoints.length) {
            combinedPoints.push(...finalLegPoints);
          } else {
            combinedPoints.push(...finalLegPoints.slice(1));
          }
        }
      }

      if (!legsData.length) {
        throw new Error('No legs were built. Verify start and checkpoint locations.');
      }

      const combinedPolyline =
        combinedPoints.length > 1 ? encode(combinedPoints) : legsData[0]?.polyline || null;

      const aggregatedRoute = {
        polyline: combinedPolyline,
        legs: legsData,
        totalDuration: totalDurationSeconds,
        totalDistanceMeters,
      };

      if (requestId !== latestBuildRequestIdRef.current) {
        return;
      }

      setRouteData(aggregatedRoute);
      lastCheckpointSigForRoute.current = checkpointSignature;

      // Check if any checkpoint is outside 10 meters of polyline and add additionalPolyline if so
      let updatedCheckpoints = checkpoints;
      if (aggregatedRoute.polyline && checkpoints && checkpoints.length) {
        const polylinePoints = decode(aggregatedRoute.polyline);
        updatedCheckpoints = checkpoints.map((cp, idx) => {
          const geometry = cp?.geometry || {};
          const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
          const isZoneStop =
            geometry?.isZone || type === 'zone' || geometry?.zoneId != null || cp?.zoneId != null;
          const zonePoints = isZoneStop ? getZonePolygonPoints(cp) : [];
          const intersectsZone =
            zonePoints.length >= 3 && polylineIntersectsPolygon(polylinePoints, zonePoints);
          if (cp.lat && cp.lng && !(isZoneStop && intersectsZone)) {
            const pt = [parseFloat(cp.lat), parseFloat(cp.lng)];
            const dist = getDistanceToPolyline(pt, polylinePoints);
            if (dist > TAIL_DISTANCE_METERS) {
              const closest = getClosestPointOnPolyline(pt, polylinePoints);
              return {
                ...cp,
                additionalEncodedPolyline: encode([pt, closest]),
              };
            }
          }
          // Remove additionalPolyline if not out of distance
          const { additionalPolyline, additionalEncodedPolyline, ...rest } = cp;
          return rest;
        });
        setCheckpoints(updatedCheckpoints);
      }

      const allStops = [origin, ...waypointStops]
        .map((stop, index) => {
          const type =
            index === 0
              ? 'origin'
              : index === stopsForRoute.length - 1
                ? 'destination'
                : 'checkpoint';
          const fallbackLabel =
            type === 'origin'
              ? 'Start'
              : type === 'destination'
                ? 'Destination'
                : `Checkpoint ${String.fromCharCode(64 + index)}`;
          const label = stop.label || fallbackLabel;
          return {
            id: stop.id || index,
            coordinates: toCoords(stop),
            label,
            address: stop.address,
            name: stop.name || stop.label || stop.address || label,
            type,
          };
        })
        .filter((s) => s.coordinates);

      setActiveTourData({
        ...(activeTourData || {}),
        ...(builderTourData || {}),
        id: activeTourData?.id || builderTourData?.id || 'draft-tour',
        name: builderTourData?.name || activeTourData?.name || 'Draft tour',
        isLooped,
        travelType: activeTourData?.travelType || builderTourData?.travelType || 'driving',
        routeOptimization:
          activeTourData?.routeOptimization || builderTourData?.routeOptimization || 'fastest',
        stops: allStops,
        encodedPolyline: aggregatedRoute.polyline,
        totalDuration: aggregatedRoute.totalDuration,
        totalDistanceMeters: aggregatedRoute.totalDistanceMeters,
        legs: aggregatedRoute.legs,
        checkpoints: updatedCheckpoints,
      });
    } catch (err) {
      if (requestId !== latestBuildRequestIdRef.current) {
        return;
      }
      setError(err.message || 'Failed to build route');
      clearRouteArtifacts();
    } finally {
      if (requestId === latestBuildRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useAutoBuild({
    autoBuildPending,
    setAutoBuildPending,
    loading,
    canBuild,
    handleBuildRoute,
    isIndoor,
  });

  useEffect(() => {
    if (lastCheckpointSigForRoute.current === null) {
      lastCheckpointSigForRoute.current = checkpointSignature;
      return;
    }
    if (checkpointSignature !== lastCheckpointSigForRoute.current) {
      setAutoBuildPending(true);
    }
  }, [checkpointSignature]);

  useEffect(() => {
    const sig = `${startLocation?.lat ?? ''}|${startLocation?.lng ?? ''}|${
      startLocation?.placeId ?? ''
    }|${startLocation?.zoneId ?? startLocation?.geometry?.zoneId ?? ''}`;
    if (sig !== lastStartSigRef.current) {
      lastStartSigRef.current = sig;
      if (routeData || activeTourData) {
        clearRouteArtifacts();
      }
      if (stops.length) setAutoBuildPending(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourData, routeData, startLocation, stops.length]);

  useEffect(() => {
    setAutoBuildPending(true);
  }, [snapToRoads]);

  useEffect(() => {
    const prevCount = lastStopCountRef.current ?? 0;
    if (stops.length > prevCount) {
      const node = scrollContainerRef.current;
      if (node) {
        const scrollToBottom = () => {
          if (typeof node.scrollTo === 'function') {
            node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
          } else {
            node.scrollTop = node.scrollHeight;
          }
        };
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(scrollToBottom);
        } else {
          scrollToBottom();
        }
      }
    }
    lastStopCountRef.current = stops.length;
  }, [stops.length]);

  useEffect(() => {
    if (builderTourData && Object.prototype.hasOwnProperty.call(builderTourData, 'name')) {
      setSaveName(builderTourData.name ?? '');
      return;
    }
    if (activeTourData && Object.prototype.hasOwnProperty.call(activeTourData, 'name')) {
      setSaveName(activeTourData.name ?? '');
    }
  }, [builderTourData?.name, activeTourData?.name]);

  const persistBuilderName = (nextValue) => {
    setSaveName(nextValue);
    // Update only the name field in builderTourData without triggering
    // checkpoint re-hydration (setBuilderTourData would call setTourForBuilder
    // which derives checkpoints from nodes, wiping any in-progress edits).
    updateBuilderTourMeta({ name: nextValue });
  };

  const persistLoopSetting = (nextValue) => {
    // isLooped is local state and passed directly to useRoutePersistence,
    // so we only need to keep builderTourData in sync for the unsaved-changes
    // signature — no checkpoint re-hydration needed.
    updateBuilderTourMeta({ isLooped: nextValue });
  };

  useEffect(() => {
    if (!isEditingName) return;
    if (nameInputRef.current) nameInputRef.current.focus();
  }, [isEditingName]);

  const { handleSaveRoute } = useRoutePersistence({
    canSaveRoute,
    editingTourId,
    routeWaypointOrder,
    checkpointMetaById,
    stopMetaById,
    routeData,
    builderTourData,
    activeTourData,
    tours,
    addTour,
    resetBuilder,
    setSaveError,
    setSaveSuccess,
    setSaving,
    onPersistSuccess,
    isLooped,
  });
  const hasTourName = saveName.trim().length > 0;
  const canPersistRoute = canSaveRoute && hasTourName;
  const saveTitle = !hasTourName
    ? 'Enter a tour name before saving'
    : canSaveRoute
      ? editingTourId
        ? 'Save changes to this tour'
        : 'Save as a new tour'
      : 'Build a tour with a valid start and end first';

  return (
    <div
      className={`bg-primary-shadow text-theme border border-theme-strong rounded shadow-lg p-1 h-100 d-flex flex-column ${className}`.trim()}
      style={{ minHeight: 0, ...style }}
    >
      <Card
        className="d-flex flex-column w-100 h-100 bg-transparent border-0 text-theme"
        style={{ minHeight: 0 }}
      >
        <Card.Body className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
          <div className="d-flex justify-content-between align-items-center flex-shrink-0 mb-3">
            <div>
              <Card.Title className="text-theme">Tour Builder</Card.Title>
              <Card.Text className="text-theme-muted small mb-0">
                Add a start plus at least one checkpoint.
              </Card.Text>
              <div className="mt-3">
                <div className="small text-theme-muted mb-1">Tour name</div>
                {isEditingName ? (
                  <div className="position-relative" style={{ maxWidth: 340 }}>
                    <Form.Control
                      ref={nameInputRef}
                      className="bg-surface-inset text-theme border-theme"
                      value={saveName}
                      onChange={(e) => {
                        persistBuilderName(e.target.value);
                      }}
                      onBlur={() => setIsEditingName(false)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === 'Escape') {
                          event.preventDefault();
                          setIsEditingName(false);
                        }
                      }}
                      placeholder="Enter a tour name"
                      size="sm"
                      style={{ paddingRight: 32 }}
                    />
                    <span
                      role="button"
                      className="text-theme-muted position-absolute top-50 translate-middle-y"
                      title="Save tour name"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setIsEditingName(false);
                      }}
                      style={{ right: 10, fontSize: 16, cursor: 'pointer' }}
                    >
                      <FaCheck />
                    </span>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-theme">{saveName || 'Draft tour'}</span>
                    <span
                      className="text-theme-muted cursor-pointer"
                      title="Edit tour name"
                      onClick={() => setIsEditingName(true)}
                      style={{ fontSize: 14 }}
                    >
                      <FaPencilAlt />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                type="switch"
                id="loop-tour-toggle"
                label="Loop Tour"
                checked={isLooped}
                className="text-theme"
                onChange={(e) => {
                  const nextValue = e.target.checked;
                  setIsLooped(nextValue);
                  persistLoopSetting(nextValue);
                  if (!nextValue) {
                    setStops((prev) => {
                      const nextStops = pruneTrailingLoopReturnStop(prev, startLocation);
                      return nextStops;
                    });
                  }
                  setAutoBuildPending(true);
                }}
              />
              <Button variant="outline-secondary" size="sm" onClick={resetBuilder}>
                Clear
              </Button>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-grow-1 overflow-auto"
            style={{ minHeight: 0 }}
          >
            <div className="d-flex flex-column gap-3 pb-2">
              <StartLocationCard
                startLocation={startLocation}
                googleApiKey={googleApiKey}
                usingCurrentStart={usingCurrentStart}
                setUsingCurrentStart={setUsingCurrentStart}
                setStartLocation={setStartLocation}
                setAutoBuildPending={setAutoBuildPending}
                setSelectedCheckpointId={setSelectedCheckpointId}
                cardId="checkpoint-editor-start"
              />

              <StopList
                stops={stops}
                googleApiKey={googleApiKey}
                dragIndex={dragIndex}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDragEnd={handleDragEnd}
                removeStop={removeStop}
                setStops={setStops}
                updateStop={updateStop}
                setAutoBuildPending={setAutoBuildPending}
                setSelectedCheckpointId={setSelectedCheckpointId}
                isIndoor={isIndoor}
                stopMetaById={stopMetaById}
                stopEtaSeconds={stopEtaSeconds}
                onAddStop={addStop}
                canAddStop={canAddStop}
                addStopDisabledReason={addStopDisabledReason}
                startLocation={startLocation}
                showLoopReturn={isLooped}
                destinationMode={normalizeTravelMode(startLocation?.returnMode)}
                onDestinationModeChange={(nextMode) => {
                  const normalizedMode = normalizeTravelMode(nextMode);
                  setStartLocation((prev) => ({
                    ...prev,
                    returnMode: normalizedMode,
                  }));
                  setAutoBuildPending(true);
                }}
              />

              {error && <div className="text-danger small">{error}</div>}
              {saveError && <div className="text-danger small">{saveError}</div>}
              {saveSuccess && <div className="text-success small">{saveSuccess}</div>}
            </div>
          </div>

          <div className="mt-auto pt-3 flex-shrink-0">
            <RouteSummaryBar
              tourEstimateLabel={tourEstimateLabel}
              tourMiles={tourMiles}
              canSaveRoute={canPersistRoute}
              saving={saving}
              onCancel={onCancelEdit}
              onSave={() => handleSaveRoute(editingTourId ? 'update' : 'new')}
              saveTitle={saveTitle}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
